import { ComponentType } from "react";
import { UpdateDto } from "./common";

// ===== TIPOS BÁSICOS =====

export interface PrivilegeDto {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePrivilegeDto {
  id: string;
  description: string;
  name: string;
  isActive: boolean;
}

export type UpdatePrivilegeDto = UpdateDto<CreatePrivilegeDto>;

export interface RoleDto {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoleDto {
  id: string;
  name: string;
  description?: string;
  organizationId?: string;
  isActive: boolean;
}

export type UpdateRoleDto = UpdateDto<CreateRoleDto>;

export interface RolePrivilegeDto {
  id: string;
  roleId: string;
  privilegeId: string;
  privilege: PrivilegeDto;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRolePrivilegeDto {
  roleId: string;
  privilegeId: string;
  isActive: boolean;
}

export type UpdateRolePrivilegeDto = UpdateDto<CreateRolePrivilegeDto>;

// ===== USER-ROLE DTOs =====

export interface UserRoleDto {
  id: string;
  name: string;
  description: string;
  userId: string;
  roleId: string;
  role?: RoleDto;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserRoleDto {
  name: string;
  id: string;
  description?: string;
  userId: string;
  roleId: string;
  isActive: boolean;
}

export type UpdateUserRoleDto = UpdateDto<CreateUserRoleDto>;

// ===== RESPUESTAS DE API =====

export interface UserRolesResponse {
  data: UserRoleDto[];
  status: string;
  code: number;
  httpErrorCode: number;
  description: string;
}

export interface RolePrivilegesResponse {
  data: RolePrivilegeDto[];
  status: string;
  code: number;
  httpErrorCode: number;
  description: string;
}

// ===== ESTADO DE AUTORIZACIÓN =====

export interface AuthorizationState {
  userRoles: UserRoleDto[];
  rolePrivileges: Record<string, RolePrivilegeDto[]>;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

// ===== CONFIGURACIÓN DE PERMISOS =====

export interface PermissionConfig {
  privilege: string;
  fallback?: boolean;
  requireAll?: boolean;
}

// ===== CONSTANTES DE PRIVILEGIOS =====

export const PRIVILEGES = {
  // User management
  READ_USER: "READ_USER",
  CREATE_USER: "CREATE_USER",
  UPDATE_USER: "UPDATE_USER",
  DELETE_USER: "DELETE_USER",

  // Organization management
  READ_ORGANIZATION: "READ_ORGANIZATION",
  CREATE_ORGANIZATION: "CREATE_ORGANIZATION",
  UPDATE_ORGANIZATION: "UPDATE_ORGANIZATION",
  DELETE_ORGANIZATION: "DELETE_ORGANIZATION",

  // Role management
  READ_ROLE: "READ_ROLE",
  CREATE_ROLE: "CREATE_ROLE",
  UPDATE_ROLE: "UPDATE_ROLE",
  DELETE_ROLE: "DELETE_ROLE",

  // Role privilege management
  READ_ROLES_PRIVILEGE: "READ_ROLES_PRIVILEGE",
  CREATE_ROLES_PRIVILEGE: "CREATE_ROLES_PRIVILEGE",
  UPDATE_ROLES_PRIVILEGE: "UPDATE_ROLES_PRIVILEGE",
  DELETE_ROLES_PRIVILEGE: "DELETE_ROLES_PRIVILEGE",

  // Inspection management
  READ_INSPECTION: "READ_INSPECTION",
  CREATE_INSPECTION: "CREATE_INSPECTION",
  UPDATE_INSPECTION: "UPDATE_INSPECTION",
  DELETE_INSPECTION: "DELETE_INSPECTION",

  // Finding management
  READ_FINDING: "READ_FINDING",
  CREATE_FINDING: "CREATE_FINDING",
  UPDATE_FINDING: "UPDATE_FINDING",
  DELETE_FINDING: "DELETE_FINDING",

  // Inspections finding (granular finding entity)
  READ_INSPECTIONS_FINDING: "READ_INSPECTIONS_FINDING",
  CREATE_INSPECTIONS_FINDING: "CREATE_INSPECTIONS_FINDING",
  UPDATE_INSPECTIONS_FINDING: "UPDATE_INSPECTIONS_FINDING",
  DELETE_INSPECTIONS_FINDING: "DELETE_INSPECTIONS_FINDING",

  // Program management
  READ_PROGRAM: "READ_PROGRAM",
  CREATE_PROGRAM: "CREATE_PROGRAM",
  UPDATE_PROGRAM: "UPDATE_PROGRAM",
  DELETE_PROGRAM: "DELETE_PROGRAM",

  // Programs version (used by Program Management view)
  READ_PROGRAMS_VERSION: "READ_PROGRAMS_VERSION",
  CREATE_PROGRAMS_VERSION: "CREATE_PROGRAMS_VERSION",
  UPDATE_PROGRAMS_VERSION: "UPDATE_PROGRAMS_VERSION",
  DELETE_PROGRAMS_VERSION: "DELETE_PROGRAMS_VERSION",

  // Program level
  READ_PROGRAM_LEVEL: "READ_PROGRAM_LEVEL",
  CREATE_PROGRAM_LEVEL: "CREATE_PROGRAM_LEVEL",
  UPDATE_PROGRAM_LEVEL: "UPDATE_PROGRAM_LEVEL",
  DELETE_PROGRAM_LEVEL: "DELETE_PROGRAM_LEVEL",

  // Sync config management
  READ_SYNC_CONFIG: "READ_SYNC_CONFIG",
  CREATE_SYNC_CONFIG: "CREATE_SYNC_CONFIG",
  UPDATE_SYNC_CONFIG: "UPDATE_SYNC_CONFIG",
  DELETE_SYNC_CONFIG: "DELETE_SYNC_CONFIG",

  // Analytics
  READ_ANALYTICS: "READ_ANALYTICS",
  CREATE_ANALYTICS: "CREATE_ANALYTICS",
  UPDATE_ANALYTICS: "UPDATE_ANALYTICS",
  DELETE_ANALYTICS: "DELETE_ANALYTICS",

  // Training
  READ_TRAINING: "READ_TRAINING",
  CREATE_TRAINING: "CREATE_TRAINING",
  UPDATE_TRAINING: "UPDATE_TRAINING",
  DELETE_TRAINING: "DELETE_TRAINING",

  // Training content progress
  READ_TRAINING_CONTENT_PROGRESS: "READ_TRAINING_CONTENT_PROGRESS",
  CREATE_TRAINING_CONTENT_PROGRESS: "CREATE_TRAINING_CONTENT_PROGRESS",
  UPDATE_TRAINING_CONTENT_PROGRESS: "UPDATE_TRAINING_CONTENT_PROGRESS",
  DELETE_TRAINING_CONTENT_PROGRESS: "DELETE_TRAINING_CONTENT_PROGRESS",

  // Reports
  READ_REPORT: "READ_REPORT",
  CREATE_REPORT: "CREATE_REPORT",
  UPDATE_REPORT: "UPDATE_REPORT",
  DELETE_REPORT: "DELETE_REPORT",

  // Report templates
  READ_REPORT_TEMPLATE: "READ_REPORT_TEMPLATE",
  CREATE_REPORT_TEMPLATE: "CREATE_REPORT_TEMPLATE",
  UPDATE_REPORT_TEMPLATE: "UPDATE_REPORT_TEMPLATE",
  DELETE_REPORT_TEMPLATE: "DELETE_REPORT_TEMPLATE",

  // Activity feed
  READ_ACTIVITY_FEED: "READ_ACTIVITY_FEED",
  CREATE_ACTIVITY_FEED: "CREATE_ACTIVITY_FEED",
  UPDATE_ACTIVITY_FEED: "UPDATE_ACTIVITY_FEED",
  DELETE_ACTIVITY_FEED: "DELETE_ACTIVITY_FEED",

  // Form management
  READ_FORM: "READ_FORM",
  CREATE_FORM: "CREATE_FORM",
  UPDATE_FORM: "UPDATE_FORM",
  DELETE_FORM: "DELETE_FORM",

  // Group management
  READ_GROUP: "READ_GROUP",
  CREATE_GROUP: "CREATE_GROUP",
  UPDATE_GROUP: "UPDATE_GROUP",
  DELETE_GROUP: "DELETE_GROUP",

  // Admin privileges
  ADMIN_ACCESS: "ADMIN_ACCESS",
  SUPER_ADMIN: "SUPER_ADMIN",
} as const;

// ===== CONSTANTES DE ROLES =====

export const ROLES = {
  // Common role names (examples - roles are dynamic per organization)
  SUPER_ADMIN: "SUPER_ADMIN",
  ADMIN: "ADMIN",
  MANAGER: "MANAGER",
  INSPECTOR: "INSPECTOR",
  VIEWER: "VIEWER",
  USER: "USER",
} as const;

// ===== TIPOS DE UNIÓN =====

export type PrivilegeName = (typeof PRIVILEGES)[keyof typeof PRIVILEGES];
export type RoleName = (typeof ROLES)[keyof typeof ROLES];

// ===== INTERFACES PARA DEBUG =====

export interface AuthorizationDebugInfo {
  currentOrganization: {
    id: string;
    name: string;
  } | null;
  pendingOrganizationId: string | null;
  userRoles: UserRoleDto[];
  activePrivileges: string[];
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  stateChangeLog: Array<{
    timestamp: string;
    action: string;
    organizationId?: string;
    organizationName?: string;
    reason?: string;
    isAuthenticated?: boolean;
    isSessionReady?: boolean;
  }>;
}

// ===== TIPOS PARA COMPONENTES =====

export interface ProtectedElementProps {
  privilege?: PrivilegeName | string;
  privileges?: (PrivilegeName | string)[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export interface ProtectedRouteProps {
  privilege?: PrivilegeName | string;
  privileges?: (PrivilegeName | string)[];
  requireAll?: boolean;
  fallback?: ComponentType;
  children: React.ReactNode;
}
